import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit-pencil.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import { ConnectorRatingInterface } from '../../interface/connector-rating.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  disableConnectorRatingFilterApplied,
  fetchAllConnectorRatings,
  fetchConnectorRatingFilters,
  setSortData,
  updateConnectorRatingFilters,
  deleteConnectorRating,
} from '../../redux/slices/ConnectorRatingSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import MyButton from '../../shared-components/button/Button';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import connectorApi from '../../utils/api/connector-rating';

import AddRating from './components/add-Rating';
import EditRating from './components/edit-Rating';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Handle edit
 */
// const handleEdit = (rowData: any) => {
//   console.log('Edit:', rowData);
// };

/**
 * Component for displaying connector rating
 * @returns  JSX.Element
 */
function ConnectorRating() {
  const { t } = useTranslation(['station', 'sharedComponent']);
  const dispatch = useAppDispatch();
  const {
    connectorRatings,
    loading,
    page,
    limit,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    sortBy,
    sortField,
    isFilterUpdated,
  } = useAppSelector((state) => state.connectorRating);
  const [editRatingOpen, setEditRatingOpen] = useState<boolean>(false);
  const [selectedRating, setSelectedRating] = useState<any>(null);
  const [addRatingOpen, setAddRatingOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');
  // const [loading] = useState<boolean>(false);
  // const [page, setPage] = useState<number>(1);
  // const [limit, setLimit] = useState<number>(10);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    notificationModal: false,
    rightCorner: '',
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  /**
   * Handle search
   * @param {any} name - Name of the search field
   * @param {any} value - Value to search
   */
  const handleSearch = (name: string, value: string) => {
    setSearchValue(value);

    dispatch(
      fetchAllConnectorRatings({
        page,
        limit,
        searchBy: value,
        sortBy,
        sortField,
      }),
    );
  };
  /**
   * Handle delete
   * @param {any} rowData - Row data
   * @param {any} event - The event
   */
  const handleDeleteWarning = (rowData: any, event: React.MouseEvent) => {
    event.stopPropagation();
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      notificationModal: true,
      content: (
        <div className="delete-rating-modal">
          <h4>Delete this rating?</h4>
          <p>Are you sure you want to delete this rating?</p>
          <span>This action cannot be undone.</span>
          <div className="delete-modal-actions">
            <MyButton
              label="Cancel"
              buttonType="outline"
              labelColorFill="#6C757D"
              onClickFunc={() => setModalBoxStatus('close')}
            />
            <MyButton
              label="Delete"
              buttonType="error"
              labelColorFill="#ffffff"
              onClickFunc={async () => {
                setModalBoxStatus('close');
                try {
                  await dispatch(deleteConnectorRating(rowData.id)).unwrap();
                  dispatch(
                    fetchAllConnectorRatings({
                      page,
                      limit,
                      searchBy: searchValue,
                      sortBy,
                      sortField,
                    }),
                  );
                } catch (error) {
                  console.error('Failed to delete rating:', error);
                }
              }}
            />
          </div>
        </div>
      ),
    });
  };

  /**
   * Edit rating function
   * @param {any} currentData - Current data
   */
  const editRatingFunction = (currentData: any) => {
    setSelectedRating(currentData);
    setEditRatingOpen(true);
  };

  const column = [
    {
      label: 'Connector Rating Name',
      accessorKey: 'connectorRatingName' as keyof ConnectorRatingInterface,
      sortAllow: true,
    },
    {
      label: 'Type',
      accessorKey: 'type' as keyof ConnectorRatingInterface,
      /**
       * Custom render for type column
       * @param {any} currentData - Current row data
       * @returns {any} Type label
       */
      customData: (currentData: ConnectorRatingInterface) => (
        <div className={currentData?.type === 'AC' ? 'ac-status' : 'dc-status'}>
          {currentData?.type}
        </div>
      ),
    },
    {
      label: 'Max Power Output',
      accessorKey: 'maxPowerOutput' as keyof ConnectorRatingInterface,
      sortAllow: true,
    },
    {
      label: 'Tariff Attached',
      accessorKey: 'tariffAttached' as keyof ConnectorRatingInterface,
    },
    {
      label: 'Connector Attached',
      accessorKey: 'connectorAttached' as keyof ConnectorRatingInterface,
    },
    {
      label: 'Status',
      accessorKey: 'status' as keyof ConnectorRatingInterface,
      /**
       * Custom render for status column
       * @param {any} currentData - Current row data
       * @returns {any} JSX Element for status
       */
      customData: (currentData: ConnectorRatingInterface) => (
        <div className={currentData?.status ? 'active-status' : 'inactive-status'}>
          <div className="status-dot" />
          {currentData?.status ? 'Active' : 'Inactive'}
        </div>
      ),
    },
    {
      label: 'Created At',
      accessorKey: 'createdAt' as keyof ConnectorRatingInterface,
      /**
       * Custom render for created at column
       * @param {any} currentData - Current row data
       * @returns {any} Formatted date string
       */
      customData: (currentData: ConnectorRatingInterface) => (
        <span>{currentData?.createdAt ? ConvertUTCtoDate(currentData.createdAt) : '-'}</span>
      ),
    },
    {
      label: 'Last Updated At',
      accessorKey: 'updatedAt' as keyof ConnectorRatingInterface,
      /**
       * Custom render for updated at column
       * @param {any} currentData - Current row data
       * @returns {any} Formatted date string
       */
      customData: (currentData: ConnectorRatingInterface) => (
        <span>{currentData?.updatedAt ? ConvertUTCtoDate(currentData.updatedAt) : '-'}</span>
      ),
    },
    {
      label: 'Actions',
      accessorKey: 'action' as keyof ConnectorRatingInterface,
      sortAllow: false,
      headingDetails: true,
      headingDescription: (
        <span className="rating-action-tooltip">
          Delete Is Available Only When No Connectors Or Tariff Plans Are Linked To This Rating.
        </span>
      ),
      /**
       * Custom render for actions column
       * @param {any} rowData - Current row data
       * @returns {any} JSX Element for actions
       */
      customData: (rowData: ConnectorRatingInterface) => {
        const canDelete =
          (rowData.tariffAttached === 0 || rowData.tariffAttached === undefined) &&
          (rowData.connectorAttached === 0 || rowData.connectorAttached === undefined);

        return (
          <div className="action-button-group">
            <div
              className="action-button-item edit-color"
              onClick={() => {
                editRatingFunction(rowData);
              }}
              role="presentation">
              <EditIcon className="action-icon edit" />
            </div>

            {canDelete ? (
              <div
                className="action-button-item delete-color"
                onClick={(event: any) => handleDeleteWarning(rowData, event)}
                role="presentation">
                <DeleteIcon className="action-icon delete" />
              </div>
            ) : (
              <div className="action-button-item disabled-action">
                <DeleteIcon className="action-icon delete" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  /**
   * Fetch connector active status API
   */
  // const handleConnectorActive = async () => {
  //   try {
  //     const response = await connectorApi.fetchConnectorActive();
  //     setActive(response?.data?.connectorActive || {});
  //   } catch (error: any) {
  //     dispatch(
  //       setNotificationMessage({
  //         message: error?.response?.data?.message || t('somethingWentWrong'),
  //         status: error?.response?.status || 500,
  //         type: 'error',
  //         code: error?.response?.data?.code,
  //       }),
  //     );
  //   }
  // };

  /**
   * Left top content for custom table
   */
  const leftTopContent = (
    <div className="connector-rating-table-header-left">
      <CustomInput
        placeholder="Search"
        name="search"
        svgIcon={<SearchIcon style={{ width: '16px', height: '16px' }} />}
        onChangeValue={(name, value) => handleSearch(name, String(value))}
        type="text"
        value={searchValue}
      />
    </div>
  );

  /**
   * Handle add rating
   */
  const handleAddRating = () => {
    setAddRatingOpen(true);
  };

  useEffect(() => {
    if (isFilterUpdated || connectorRatings.length === 0) {
      dispatch(
        fetchAllConnectorRatings({
          page,
          limit,
        }),
      );

      dispatch(disableConnectorRatingFilterApplied());
    }
  }, [filters]);

  /**
   * Top left content for page title (counts)
   */
  const topLeftContent = (
    <div className="connector-rating-list-top-left-content">
      {/* <div className="counts">
        <p className="count">{connectorRatingList.length}</p>
      </div> */}
      <div className="status active">
        <div className="indicator" />
        <p className="label">{t('active')}</p>
        <p className="count">{connectorRatings.filter((item) => item.status).length}</p>
      </div>
      <div className="status inactive">
        <div className="indicator" />
        <p className="label">{t('inactive')}</p>
        <p className="count">{connectorRatings.filter((item) => !item.status).length}</p>
      </div>
    </div>
  );

  // useEffect(() => {
  //   handleConnectorActive();
  // });

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: 'Connector Rating',
        icon: '',
        count: totalResults,
      }),
    );
  }, [dispatch, totalResults]);
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo', { defaultValue: 'S.No' }), accessorKey: '__serial__' },
    {
      label: t('connectorRatingName', { defaultValue: 'Connector Rating Name' }),
      accessorKey: 'connectorRatingName',
    },
    { label: t('type', { defaultValue: 'Type' }), accessorKey: 'type' },
    {
      label: t('maxPowerOutput', { defaultValue: 'Max Power Output' }),
      accessorKey: 'maxPowerOutput',
    },
    {
      label: t('tariffAttached', { defaultValue: 'Tariff Attached' }),
      accessorKey: 'tariffAttached',
    },
    {
      label: t('connectorAttached', { defaultValue: 'Connector Attached' }),
      accessorKey: 'connectorAttached',
    },
    { label: t('status', { defaultValue: 'Status' }), accessorKey: 'status' },
    { label: t('createdAt', { defaultValue: 'Created At' }), accessorKey: 'createdAt' },
    { label: t('updatedAt', { defaultValue: 'Last Updated' }), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function to export data in PDF or CSV format
   * @param {string} type - The export format type ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: Record<string, any> = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    if (searchValue) payload.searchBy = searchValue;

    connectorApi
      .exportConnectorRatings(removeEmptyKeys(payload))
      .then((response: any) => {
        const exportDataList = Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || response?.results || [];

        const headers = exportCurrentColumns.map((col) => col.label);

        const rows = exportDataList.map((row: any, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return String(index + 1);

              case 'connectorRatingName':
                return row?.connectorRatingName || row?.name || 'N/A';

              case 'type':
                return row?.type || row?.currentType || 'N/A';

              case 'maxPowerOutput': {
                if (row?.maxPowerOutput !== undefined && row?.maxPowerOutput !== null) {
                  return String(row.maxPowerOutput);
                }
                if (row?.capacityKW !== undefined && row?.capacityKW !== null) {
                  return String(row.capacityKW);
                }
                return 'N/A';
              }

              case 'tariffAttached':
                return row?.tariffAttached !== undefined ? String(row.tariffAttached) : 'N/A';

              case 'connectorAttached':
                return row?.connectorAttached !== undefined ? String(row.connectorAttached) : 'N/A';

              case 'status':
                return row?.status ? 'Active' : 'Inactive';

              case 'createdAt':
                return row?.createdAt ? ConvertUTCtoDateTime(row.createdAt) : 'N/A';

              case 'updatedAt':
                return row?.updatedAt ? ConvertUTCtoDateTime(row.updatedAt) : 'N/A';

              default:
                return row?.[col.accessorKey] ?? 'N/A';
            }
          }),
        );

        setExportLoader(false);

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'connector-ratings.csv');
          } else {
            exportPDF(headers, rows, 'connector-ratings.pdf');
          }
        }
      })
      .catch((error: any) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('errorMessage', 'Something went wrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || error?.response?.status || 500,
          }),
        );
      });
  };

  return (
    <div className="connector-rating-list-wrapper">
      <PageTitle
        title={t('Total Connector Ratings')}
        topLeftContent={topLeftContent}
        buttonType="primary"
        buttonSvgIcon={<PlusIcon />}
        buttonLabel="Add Rating"
        onClickButton={handleAddRating}
      />

      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={false}
      />

      <CustomTable
        dataKey="connectorRating"
        header={column}
        data={connectorRatings}
        loading={loading}
        page={page}
        totalPages={totalPages}
        limit={limit}
        totalResults={totalResults}
        serialNo
        version="v2"
        actionButton
        refreshRequired
        height={window.innerHeight >= 700 ? 332 : 288}
        disableSearch
        leftTopContent={leftTopContent}
        pageLimitChange={(value: any) => {
          dispatch(
            fetchAllConnectorRatings({
              page: value.page,
              limit: value.limit,
              sortBy,
              sortField,
              searchBy: searchValue,
            }),
          );
        }}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={(sortData: any) => {
          dispatch(setSortData(sortData));

          dispatch(
            fetchAllConnectorRatings({
              page,
              limit,
              searchBy: searchValue,
              sortBy: sortData.sortBy,
              sortField: sortData.sortField,
            }),
          );
        }}
        onHiddenColumnsChange={setHiddenColumns}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateConnectorRatingFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConnectorRatingFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        handleRefresh={() => {}}
        refershCurrentPages={() => {}}
        hoverLabel="Refresh"
        sidePanelEnabled={addRatingOpen || editRatingOpen}
        sidePanelManualContent={
          addRatingOpen ? (
            <AddRating
              closeModal={() => {
                setAddRatingOpen(false);
              }}
            />
          ) : (
            <EditRating
              ratingDetails={selectedRating}
              closeModal={() => {
                setEditRatingOpen(false);
              }}
            />
          )
        }
        onSidePanelClose={() => {
          setAddRatingOpen(false);
          setEditRatingOpen(false);
        }}
      />
    </div>
  );
}

export default ConnectorRating;
